import React, {useEffect} from 'react';
import * as Yup from 'yup';
import {Modal, Row, Col} from 'react-bootstrap';
import {useDispatch, useSelector} from 'react-redux';
import {Input, Form, MainLoader, RadioField} from '@components';
import {Form as FormRender} from 'formik';
import {
  WhiteButton,
  GreenButton,
  StyledH3,
  StyledLabel,
  StyledRequiredLabel,
  StyledError,
} from '@styled';
import {useTranslation} from 'react-i18next';
import {get} from 'lodash';
import {sagaActions} from './redux/saga';
import {resetUpdateCorpSuccess} from './redux/index';

const CorporateRequestUpdate = ({data, onHide}) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const isLoading = useSelector((state) => state.helpdesk.updateCorpReqLoading);
  const savedSuccess = useSelector((state) => state.helpdesk.updateCorpSuccess);

  const handleSubmit = (values) => {
    dispatch({
      type: sagaActions.UPDATE_CORPORATE_REQUEST,
      payload: {
        values: {...values, corpId: data.id},
      },
    });
  };

  useEffect(() => {
    if (savedSuccess) {
      dispatch(resetUpdateCorpSuccess());
      onHide();
    }
  }, [savedSuccess]);

  return (
    <Modal
      show
      onHide={onHide}
      centered
      size="lg"
      keyboard={false}
      backdrop="static"
    >
      <Modal.Header closeButton>
        <StyledH3>{t('owner.corporateRequests.requestUpdateTitle')}</StyledH3>
      </Modal.Header>
      <Modal.Body>
        {isLoading && (
          <MainLoader
            minHeight="100vh"
            spinnerColor="#479541"
            background="rgba(0,0,0,0.2)"
          />
        )}
        <Form
          initialValues={{
            comments: '',
            status: '',
          }}
          validationSchema={Yup.object({
            comments: Yup.string().when('status', {
              is: (cc) => cc === 'Rejected',
              then: Yup.string().required(
                t('owner.corporateRequests.required.remarks')
              ),
            }),
            status: Yup.string().required(
              t('owner.corporateRequests.required.status')
            ),
          })}
          onSubmit={(values) => {
            handleSubmit(values);
          }}
        >
          {({values, errors}) => {
            return (
              <FormRender noValidate className="reactForm">
                <Row>
                  <Col className="text-muted font-weight-bold">
                    {t('owner.corporateRequests.corporateDetails')}
                  </Col>
                </Row>
                <Row>
                  <Col xs={6} lg={6}>
                    {t('owner.corporateRequests.corporateName')}
                  </Col>
                  <Col xs={6} lg={6}>
                    {get(data, 'name', '-')}
                  </Col>
                </Row>
                <Row>
                  <Col xs={6} lg={6}>
                    {t(
                      'owner.corporateRequests.corporateCommercialRegistration'
                    )}
                  </Col>
                  <Col xs={6} lg={6}>
                    {get(data, 'commercialRegistration', '-')}
                  </Col>
                </Row>
                <Row>
                  <Col xs={6} lg={6}>
                    {t('owner.corporateRequests.tradingName')}
                  </Col>
                  <Col xs={6} lg={6}>
                    {get(data, 'tradingName', '-')}
                  </Col>
                </Row>
                <Row>
                  <Col className="text-muted font-weight-bold mt-3">
                    {t('owner.corporateRequests.address')}
                  </Col>
                </Row>
                <Row>
                  <Col xs={6} lg={6}>
                    {t('owner.corporateRequests.streetAddress')}
                  </Col>
                  <Col xs={6} lg={6}>
                    {get(data, 'streetAddress', '-')}
                  </Col>
                </Row>
                <Row>
                  <Col xs={6} lg={6}>
                    {t('owner.corporateRequests.postalAddress')}
                  </Col>
                  <Col xs={6} lg={6}>
                    {get(data, 'postalAddress', '-')}
                  </Col>
                </Row>
                <Row>
                  <Col className="text-muted font-weight-bold mt-3">
                    {t('owner.corporateRequests.executiveDetails')}
                  </Col>
                </Row>
                <Row>
                  <Col xs={6} lg={6}>
                    {t('owner.corporateRequests.executiveFullName')}
                  </Col>
                  <Col xs={6} lg={6}>
                    {get(data, 'executive.fullName', '-')}
                  </Col>
                </Row>
                <Row>
                  <Col xs={6} lg={6}>
                    {t('owner.corporateRequests.executiveEmail')}
                  </Col>
                  <Col xs={6} lg={6}>
                    {get(data, 'executive.email', '-')}
                  </Col>
                </Row>
                <Row>
                  <Col xs={6} lg={6}>
                    {t('owner.corporateRequests.executivePhone')}
                  </Col>
                  <Col xs={6} lg={6}>
                    {get(data, 'executive.phone', '-')}
                  </Col>
                </Row>
                <Row>
                  <Col xs={6} lg={6}>
                    {t('owner.corporateRequests.executivePosition')}
                  </Col>
                  <Col xs={6} lg={6}>
                    {get(data, 'executive.position', '-')}
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <StyledLabel>
                      {t('owner.corporateRequests.status')}
                    </StyledLabel>
                    <StyledRequiredLabel>*</StyledRequiredLabel>
                    <div className="d-flex">
                      <RadioField
                        id="isApproved"
                        name="status"
                        label={t('owner.corporateRequests.statusIsApproved')}
                        placeholder=""
                        className="mr-3"
                        value="Approved"
                      />
                      <RadioField
                        id="isRejected"
                        name="status"
                        label={t('owner.corporateRequests.statusIsRejected')}
                        placeholder=""
                        value="Rejected"
                      />
                    </div>
                    {errors.status && (
                      <StyledError
                        style={{marginTop: '-10px', marginBottom: '10px'}}
                      >
                        {errors.status}
                      </StyledError>
                    )}
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col>
                    <Input
                      as="textarea"
                      name="comments"
                      id="comments"
                      label={t('owner.corporateRequests.remarks')}
                      placeholder=""
                      rows={5}
                      required={values.status === 'Rejected' ? true : false}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <WhiteButton
                      type="button"
                      className="ml-2"
                      onClick={onHide}
                    >
                      {t('owner.corporateRequests.cancelBtn')}
                    </WhiteButton>
                    <GreenButton type="submit" className="ml-2">
                      {t('owner.corporateRequests.submitBtn')}
                    </GreenButton>
                    {/* <WhiteButton type="button" className="ml-2">
                Reject
              </WhiteButton> */}
                  </Col>
                </Row>
              </FormRender>
            );
          }}
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default CorporateRequestUpdate;
